import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, ArrowLeft, Loader2, Gem, Shield, Check } from 'lucide-react';
import { api, LitCraftStoreProduct } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query-keys';
import { useStoreProducts } from '../hooks';
import { useSubscription } from '../hooks';

export function StorePage() {
  const { gemBundles, subscriptions, loading, error: storeError } = useStoreProducts();
  const { subscription } = useSubscription();
  const queryClient = useQueryClient();

  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [confirmProduct, setConfirmProduct] = useState<LitCraftStoreProduct | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const error = storeError || actionError;

  useEffect(() => {
    api
      .getCreditBalance()
      .then((b) => setCreditBalance(b.balance))
      .catch(() => {
        /* balance display is optional — fail silently */
      });
  }, []);

  const handlePurchase = async (product: LitCraftStoreProduct) => {
    setPurchasing(product.productIdentifier);
    setActionError(null);
    setSuccessMessage(null);
    try {
      const result = await api.purchaseLitCraftStoreProduct(product.productIdentifier);
      setCreditBalance(result.newBalance);
      // Invalidate subscription cache if a subscription was purchased
      if (result.subscription) {
        queryClient.invalidateQueries({ queryKey: queryKeys.gameRewards.subscription() });
      }
      if (result.gemsAwarded) {
        setSuccessMessage(`Purchased ${result.gemsAwarded} Lit Gems!`);
      } else if (result.subscription) {
        setSuccessMessage(
          `${result.subscription.tier.charAt(0).toUpperCase() + result.subscription.tier.slice(1)} Lit Pass activated!`,
        );
      }
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'Purchase failed');
    } finally {
      setPurchasing(null);
      setConfirmProduct(null);
    }
  };

  const handleClick = (product: LitCraftStoreProduct) => {
    if (confirmProduct?.productIdentifier === product.productIdentifier) {
      handlePurchase(product);
    } else {
      setConfirmProduct(product);
      setSuccessMessage(null);
      setActionError(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_game_rewards"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Dashboard
        </Link>
        <h1 className="text-2xl font-semibold">LitCraft Store</h1>
        {creditBalance !== null && (
          <p className="text-sm text-muted-foreground mt-1">
            Credit Balance: {creditBalance.toFixed(0)} credits (${(creditBalance / 100).toFixed(2)})
          </p>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-md border border-destructive bg-destructive/10 p-3">
          <AlertCircle className="h-4 w-4 text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
        </div>
      )}

      {successMessage && (
        <div className="flex items-center gap-2 rounded-md border border-green-500 bg-green-500/10 p-3">
          <Check className="h-4 w-4 text-green-600" />
          <p className="text-sm text-green-600">{successMessage}</p>
        </div>
      )}

      {/* Gem Bundles */}
      <div>
        <h2 className="text-lg font-medium mb-3 flex items-center gap-2">
          <Gem className="h-5 w-5" />
          Lit Gem Bundles
        </h2>
        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-36 rounded-lg" />
            ))}
          </div>
        ) : gemBundles.length === 0 ? (
          <p className="text-sm text-muted-foreground">No gem bundles available.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {gemBundles.map((product) => {
              const gemCount = (product.metadata as { gem_quantity?: number }).gem_quantity ?? 0;
              const isConfirming = confirmProduct?.productIdentifier === product.productIdentifier;
              const isPurchasing = purchasing === product.productIdentifier;

              return (
                <Card key={product.productId} className="relative">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">{gemCount} Lit Gems</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-2xl font-bold">${product.priceUsd}</p>
                    <p className="text-xs text-muted-foreground">{product.priceCredits} credits</p>
                    <Button
                      size="sm"
                      className="w-full"
                      variant={isConfirming ? 'default' : 'outline'}
                      disabled={isPurchasing}
                      onClick={() => handleClick(product)}
                    >
                      {isPurchasing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      {isConfirming ? 'Confirm Purchase' : 'Buy'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Direct Subscriptions */}
      <div>
        <h2 className="text-lg font-medium mb-3 flex items-center gap-2">
          <Shield className="h-5 w-5" />
          Lit Passes (Direct Purchase)
        </h2>
        {subscription && (
          <p className="text-sm text-muted-foreground mb-3">
            Current: {subscription.tier.charAt(0).toUpperCase() + subscription.tier.slice(1)} tier
            (expires {new Date(subscription.expiresAt).toLocaleDateString()})
          </p>
        )}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-44 rounded-lg" />
            ))}
          </div>
        ) : subscriptions.length === 0 ? (
          <p className="text-sm text-muted-foreground">No subscriptions available.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {subscriptions.map((product) => {
              const tier = (product.metadata as { tier?: string }).tier ?? '';
              const isConfirming = confirmProduct?.productIdentifier === product.productIdentifier;
              const isPurchasing = purchasing === product.productIdentifier;

              return (
                <Card key={product.productId} className="relative">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base">
                      {tier.charAt(0).toUpperCase() + tier.slice(1)} Lit Pass
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">30 days</p>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-2xl font-bold">${product.priceUsd}</p>
                    <p className="text-xs text-muted-foreground">
                      Skip Lit Gems — buy directly with credits
                    </p>
                    <Button
                      size="sm"
                      className="w-full"
                      variant={isConfirming ? 'default' : 'outline'}
                      disabled={isPurchasing}
                      onClick={() => handleClick(product)}
                    >
                      {isPurchasing && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                      {isConfirming ? 'Confirm Purchase' : 'Buy'}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
